/**
 * The voice cap (05 §2): at most `MAX_VOICES` effect voices sound at once. When a new one needs
 * room, the least important goes, and among equals the oldest. A newcomer less important than
 * everything playing is simply not played. Pure.
 */

export const MAX_VOICES = 8;

export interface Slot {
  /** Audio-clock seconds the voice starts and falls silent. */
  readonly start: number;
  readonly end: number;
  /** Higher is more important (see `PRIORITY` in scan.ts). */
  readonly priority: number;
}

export type Admission =
  | { readonly kind: 'room' }
  | { readonly kind: 'steal'; readonly index: number }
  | { readonly kind: 'drop' };

/** The voices still sounding at `now`. */
export function expire<T extends Slot>(slots: readonly T[], now: number): T[] {
  return slots.filter((s) => s.end > now);
}

/** Whether a new voice of `priority` fits, and if not, which playing voice to steal. */
export function admit(slots: readonly Slot[], priority: number, cap = MAX_VOICES): Admission {
  if (slots.length < cap) return { kind: 'room' };
  let victim = -1;
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    const v = victim >= 0 ? slots[victim] : undefined;
    if (!s) continue;
    if (!v || s.priority < v.priority || (s.priority === v.priority && s.start < v.start)) victim = i;
  }
  const v = slots[victim];
  if (!v || v.priority > priority) return { kind: 'drop' };
  return { kind: 'steal', index: victim };
}
